import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, CalendarClock, ShieldPlus, Navigation, Globe, HandCoins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";

const TYPES = [
  { id: "operator", label: "Our collection", hint: "Platform operator · GH₵2 / seat", icon: HandCoins },
  { id: "tracking", label: "Tracking", hint: "Tracking fee · GH₵3 / seat", icon: Navigation },
  { id: "online", label: "Online charge", hint: "Online booking fee · GH₵5 / seat", icon: Globe },
  { id: "insurance", label: "Insurance", hint: "Travel insurance · GH₵2 / passenger", icon: ShieldPlus },
];
const FREQS = [["daily", "Every day"], ["weekly", "Weekly"], ["monthly", "Monthly"]];
const DAYS = [["0", "Monday"], ["1", "Tuesday"], ["2", "Wednesday"], ["3", "Thursday"], ["4", "Friday"], ["5", "Saturday"], ["6", "Sunday"]];

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

export default function Reports() {
  const [type, setType] = useState("operator");
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/reports/${type}`, { params: { date_from: from, date_to: to } });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [type, from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/reports/config").then(({ data: d }) => setCfg(d)).catch(() => {}); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/admin/reports/email", { type, date_from: from, date_to: to, email: email.trim() || null });
      toast.success(`Report emailed to ${r.recipients.join(", ")}`);
      setEmail("");
      api.get("/admin/reports/config").then(({ data: d }) => setCfg(d)).catch(() => {});
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const saveCfg = async () => {
    if (!(cfg.schedule.types || []).length) { toast.error("Select at least one report to include"); return; }
    setSavingCfg(true);
    try {
      const recipients = (cfg.recipients || []).map((x) => x.trim()).filter(Boolean);
      const { data: d } = await api.put("/admin/reports/config", { recipients, schedule: cfg.schedule });
      setCfg(d);
      toast.success("Scheduled email settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingCfg(false); }
  };

  const setSched = (patch) => setCfg((c) => ({ ...c, schedule: { ...c.schedule, ...patch } }));
  const toggleType = (id) => setSched({ types: cfg.schedule.types.includes(id) ? cfg.schedule.types.filter((t) => t !== id) : [...cfg.schedule.types, id] });

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h2 className="font-display text-2xl font-bold">Collection reports</h2>
        <p className="text-sm text-slate-500">Day-by-day totals from every paid ticket in the selected dates.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="report-type-tabs">
        {TYPES.map((t) => (
          <button key={t.id} onClick={() => setType(t.id)} data-testid={`report-type-${t.id}`}
            className={`flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-left text-sm font-semibold transition ${type === t.id ? "border-[#0052FF] bg-[#0052FF] text-white shadow-[0_8px_18px_-8px_rgba(0,82,255,0.8)]" : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-[#0052FF]"}`}>
            <t.icon className="h-4 w-4 shrink-0" />
            <span>{t.label}<span className={`block text-[11px] font-normal ${type === t.id ? "text-blue-100" : "text-slate-400"}`}>{t.hint}</span></span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
        <label className="text-sm font-semibold text-slate-600">From<Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-44 bg-white" data-testid="report-from" /></label>
        <label className="text-sm font-semibold text-slate-600">To<Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="mt-1 w-44 bg-white" data-testid="report-to" /></label>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="report-refresh">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}</Button>
      </div>

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-3" data-testid="report-summary">
            <Stat label="Total collected" value={money(data.total)} testid="report-total" />
            <Stat label="Tickets" value={data.tickets} testid="report-tickets" />
            <Stat label="Seats" value={data.seats} testid="report-seats" />
          </div>

          {data.type === "tracking" && (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="tracking-split">
              <Stat label={`Tracker installed · ${data.tracking_company_name}`} value={`${money(data.installed_total)} · ${data.installed_seats} seats`} testid="tracking-installed" />
              <Stat label={`No tracker · ${data.consolidated_fund_name}`} value={`${money(data.not_installed_total)} · ${data.not_installed_seats} seats`} testid="tracking-not-installed" />
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-slate-100">
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Tickets</th><th className="px-4 py-3 text-right">Seats</th><th className="px-4 py-3 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {data.days.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400" data-testid="report-empty">No paid tickets in this period.</td></tr>}
                {data.days.map((d) => (
                  <tr key={d.date} className="border-t border-slate-100" data-testid={`report-row-${d.date}`}>
                    <td className="px-4 py-2.5 font-medium">{d.date}</td>
                    <td className="px-4 py-2.5 text-right">{d.tickets}</td>
                    <td className="px-4 py-2.5 text-right">{d.seats}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{money(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
              {data.days.length > 0 && (
                <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50 font-bold"><td className="px-4 py-3">Total</td><td className="px-4 py-3 text-right">{data.tickets}</td><td className="px-4 py-3 text-right">{data.seats}</td><td className="px-4 py-3 text-right text-[#0052FF]">{money(data.total)}</td></tr></tfoot>
              )}
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/50 p-4" data-testid="report-email-row">
            <Mail className="h-5 w-5 text-[#0052FF]" />
            <span className="text-sm font-semibold text-slate-700">Email this report</span>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Recipient email (optional)" className="w-72 bg-white" data-testid="report-email-input" />
            <Button onClick={sendNow} disabled={sending} className="bg-[#0052FF] hover:bg-[#0047db]" data-testid="report-email-send">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send now"}</Button>
          </div>
          <p className="-mt-1 text-xs text-slate-400" data-testid="report-email-hint">Leave the email blank to send to your saved scheduled recipients.</p>
        </>
      )}

      {cfg && (
        <div className="space-y-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="report-schedule">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#0052FF]" /><h3 className="font-display text-lg font-bold">Scheduled email reports</h3></div>
            <Switch checked={cfg.schedule.enabled} onCheckedChange={(v) => setSched({ enabled: v })} data-testid="schedule-enabled" />
          </div>
          <p className="text-xs text-slate-500">When on, the selected reports are emailed automatically — daily for yesterday, weekly for the past 7 days, or monthly for the previous month.</p>

          <label className="block text-sm font-semibold text-slate-600">Recipients (one email per line)
            <textarea value={(cfg.recipients || []).join("\n")} onChange={(e) => setCfg((c) => ({ ...c, recipients: e.target.value.split("\n") }))}
              rows={3} placeholder="finance@gprtu.gh" className="mt-1 w-full rounded-xl border border-slate-200 p-2.5 text-sm" data-testid="schedule-recipients" />
          </label>

          <div className="flex flex-wrap gap-4">
            <label className="text-sm font-semibold text-slate-600">Frequency
              <select value={cfg.schedule.frequency} onChange={(e) => setSched({ frequency: e.target.value })} className="mt-1 block w-44 rounded-xl border border-slate-200 p-2.5 text-sm" data-testid="schedule-frequency">
                {FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            {cfg.schedule.frequency === "weekly" && (
              <label className="text-sm font-semibold text-slate-600">On
                <select value={String(cfg.schedule.weekday)} onChange={(e) => setSched({ weekday: Number(e.target.value) })} className="mt-1 block w-44 rounded-xl border border-slate-200 p-2.5 text-sm" data-testid="schedule-weekday">
                  {DAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            )}
          </div>

          <div>
            <div className="label-xs mb-2">Reports to include</div>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button key={t.id} onClick={() => toggleType(t.id)} data-testid={`schedule-type-${t.id}`}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${cfg.schedule.types.includes(t.id) ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={saveCfg} disabled={savingCfg} className="bg-[#0052FF] hover:bg-[#0047db]" data-testid="schedule-save">{savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save schedule"}</Button>
        </div>
      )}
    </div>
  );
}
